import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IbanField } from "@/components/IbanField";
import { isValidIban } from "@/lib/iban";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MapPin, Plus, Pencil } from "lucide-react";

const FIELD = (label, key, data, onChange, extra = {}) => (
  <div>
    <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
    <Input data-testid={`resident-field-${key}`} value={data[key] || ""} onChange={(e) => onChange({ ...data, [key]: e.target.value })} className="h-9 mt-1" {...extra} />
  </div>
);

// Campo Provincia con auto-compilazione dalla città (Google Places lato server)
export const ProvinciaField = ({ data, onChange, compact = false }) => {
  const manual = useRef(false);
  const [loading, setLoading] = useState(false);
  const citta = data.citta || "";
  useEffect(() => {
    if (manual.current) return;
    const c = citta.trim();
    if (c.length < 2) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/geocode/provincia?citta=${encodeURIComponent(c)}`);
        if (r.data?.provincia && !manual.current) onChange((prev) => ({ ...prev, provincia: r.data.provincia }));
      } catch { /* silenzioso */ } finally { setLoading(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [citta]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      {!compact && <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Provincia (auto dalla città)</label>}
      <div className="relative">
        <Input data-testid="building-field-provincia" value={data.provincia || ""} maxLength={2}
          onChange={(e) => { manual.current = true; onChange((prev) => ({ ...prev, provincia: e.target.value.toUpperCase().slice(0, 2) })); }}
          placeholder={compact ? "Provincia" : "es. MI"} className={`h-9 font-mono uppercase ${compact ? "" : "mt-1"}`} />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      </div>
    </div>
  );
};

// Campo Indirizzo con autocompletamento Google Places (auto-compila anche città, CAP, provincia)
export const AddressAutocompleteField = ({ data, onChange, label = "Indirizzo *", compact = false, placeholder }) => {
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);
  const sessRef = useRef(window.crypto?.randomUUID?.() || String(Math.random()));
  const boxRef = useRef(null);
  const val = data.address || "";

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onType = (v) => {
    onChange((prev) => ({ ...prev, address: v }));
    if (debRef.current) clearTimeout(debRef.current);
    if (!v || v.trim().length < 3) { setResults([]); setOpenList(false); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/geocode/autocomplete?q=${encodeURIComponent(v)}&session_token=${sessRef.current}`);
        setResults(r.data?.suggestions || []); setOpenList(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
  };

  const pick = async (s) => {
    setOpenList(false);
    try {
      const r = await api.get(`/geocode/details/${s.place_id}?session_token=${sessRef.current}`);
      const d = r.data || {};
      onChange((prev) => ({
        ...prev,
        address: d.via || d.formatted_address || s.text || prev.address,
        citta: d.citta || prev.citta,
        cap: d.cap || prev.cap,
        provincia: d.provincia || prev.provincia,
      }));
    } catch {
      onChange((prev) => ({ ...prev, address: s.text || s.main_text || prev.address }));
    }
    sessRef.current = window.crypto?.randomUUID?.() || String(Math.random());
  };

  return (
    <div ref={boxRef} className="relative">
      {!compact && <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>}
      <div className="relative">
        <Input data-testid="building-field-address" value={val} autoComplete="off"
          onChange={(e) => onType(e.target.value)}
          onFocus={() => { if (results.length) setOpenList(true); }}
          placeholder={placeholder || (compact ? "Indirizzo *" : "Inizia a scrivere: via e numero civico…")} className={`h-9 pr-8 ${compact ? "" : "mt-1"}`} />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      </div>
      {openList && results.length > 0 && (
        <div data-testid="building-address-suggestions" className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-56 overflow-y-auto">
          {results.map((s) => (
            <button key={s.place_id} type="button" data-testid={`building-address-opt-${s.place_id}`}
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-start gap-2 transition-colors">
              <MapPin className="h-3.5 w-3.5 text-[#007BFF] shrink-0 mt-0.5" />
              <span className="min-w-0"><span className="font-medium">{s.main_text || s.text}</span>{s.secondary_text ? <span className="text-muted-foreground"> · {s.secondary_text}</span> : null}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ResidentDialog = ({ state, onChange, onClose, onSave, saving }) => {
  const data = state?.data || {};
  const immList = Array.isArray(data.catasto_immobili) ? data.catasto_immobili : [];
  const imm0 = immList[0] || {};
  const setImm = (patch) => {
    const cur = immList.map((x) => ({ ...x }));
    cur[0] = { ...(cur[0] || {}), ...patch };
    onChange({ ...data, catasto_immobili: cur, id_immobile: cur[0].id_immobile || data.id_immobile });
  };
  const CATFIELD = (label, key, extra = {}) => (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
      <Input value={imm0[key] ?? ""} onChange={(e) => setImm({ [key]: e.target.value })}
        data-testid={`resident-catasto-${key}`} className="h-9 mt-1" {...extra} />
    </div>
  );
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="resident-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{state?.mode === "add" ? "Aggiungi condòmino" : "Modifica condòmino"}</DialogTitle>
          <DialogDescription>Inserisci i dati anagrafici, i contatti e i dati dell'immobile.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{FIELD("Nominativo *", "nome", data, onChange)}</div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Ruolo</label>
            <select data-testid="resident-field-ruolo" value={data.ruolo || ""} onChange={(e) => onChange({ ...data, ruolo: e.target.value })} className="w-full h-9 mt-1 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">—</option>
              <option value="proprietario">Proprietario</option>
              <option value="inquilino">Inquilino</option>
            </select>
          </div>
          {FIELD("Millesimi", "millesimi", data, onChange)}
          {FIELD("Scala", "scala", data, onChange)}
          {FIELD("Interno", "interno", data, onChange)}
          {FIELD("Codice Fiscale", "codice_fiscale", data, onChange, { className: "h-9 mt-1 font-mono uppercase" })}
          {FIELD("Quota (€)", "quota", data, onChange)}
          {FIELD("Email", "email", data, onChange, { type: "email" })}
          {FIELD("Telefono", "telefono", data, onChange)}
          <div className="col-span-2 mt-1 border-t border-border pt-3">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Dati catastali immobile</div>
            <div className="grid grid-cols-4 gap-3">
              {CATFIELD("Foglio", "foglio")}
              {CATFIELD("Particella", "particella")}
              {CATFIELD("Sub.", "subalterno")}
              {CATFIELD("Categoria", "categoria", { className: "h-9 mt-1 uppercase" })}
            </div>
            {immList.length > 1 && (
              <p className="text-[11px] text-amber-600 mt-1">Questo condòmino ha {immList.length} immobili: qui modifichi il primo.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button data-testid="save-resident-btn" onClick={onSave} disabled={saving} className="bg-[#007BFF] hover:bg-[#0056B3]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const NewBuildingButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({ name: "", address: "", citta: "", provincia: "", cap: "", codice_fiscale: "", iban: "", sdi: "" });
  const [saving, setSaving] = useState(false);
  const allFilled = d.name.trim() && d.address.trim() && d.citta.trim() && d.cap.trim() && d.codice_fiscale.trim() && d.iban.trim() && isValidIban(d.iban);
  const save = async () => {
    if (!d.name.trim() || !d.address.trim() || !d.citta.trim() || !d.cap.trim() || !d.codice_fiscale.trim() || !d.iban.trim()) return toast.error("Nome, indirizzo, città, CAP, codice fiscale e IBAN sono obbligatori");
    if (!isValidIban(d.iban)) return toast.error("L'IBAN inserito non è valido: controlla formato e cifre di controllo.");
    setSaving(true);
    try {
      const r = await api.post("/buildings", d);
      toast.success("Condominio creato");
      if (!r.data.subscribed) {
        setTimeout(() => toast.warning("Questo condominio non è collegato. Attivalo dalla sezione \"Acquisti e collegamento\" per usarlo con tutte le funzioni.", { duration: 8000 }), 500);
      }
      if (r.data.demo_purged) setTimeout(() => toast.info("Dati demo rimossi: ora vedi i tuoi dati reali."), 600);
      setOpen(false); setD({ name: "", address: "", citta: "", provincia: "", cap: "", codice_fiscale: "", iban: "", sdi: "" });
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.status === 402 ? "Limite condomìni raggiunto: attiva un condominio aggiuntivo dalla fatturazione." : (e.response?.data?.detail || "Errore creazione"));
    } finally { setSaving(false); }
  };
  return (
    <>
      <Button data-testid="new-building-btn" onClick={() => setOpen(true)} className="bg-[#007BFF] hover:bg-[#0056B3] rounded-md h-11"><Plus className="h-4 w-4 mr-1" />Nuovo condominio</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-building-dialog">
          <DialogHeader><DialogTitle>Nuovo condominio</DialogTitle>
            <DialogDescription>Tutti i campi sono obbligatori. Potrai inserire l'anagrafica condòmini in un secondo momento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {FIELD("Nome *", "name", d, setD)}
            <AddressAutocompleteField data={d} onChange={setD} label="Indirizzo *" />
            {FIELD("Città *", "citta", d, setD)}
            <ProvinciaField data={d} onChange={setD} />
            {FIELD("CAP *", "cap", d, setD)}
            {FIELD("Codice Fiscale *", "codice_fiscale", d, setD, { className: "h-9 mt-1 font-mono uppercase" })}
            <IbanField label="IBAN" required testId="building-iban" value={d.iban} onChange={(v) => setD((p) => ({ ...p, iban: v }))} />
            {FIELD("Codice SDI (facoltativo)", "sdi", d, setD, { className: "h-9 mt-1 font-mono uppercase" })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-building-btn" onClick={save} disabled={saving || !allFilled} className="bg-[#007BFF] hover:bg-[#0056B3]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EditBuildingButton = ({ building, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setD({ name: building.name || "", address: building.address || "", citta: building.citta || "", provincia: building.provincia || "", codice_fiscale: building.codice_fiscale || "", iban: building.iban || "", sdi: building.sdi || "" }); }, [open, building]);
  const save = async () => {
    if ((d.iban || "").trim() && !isValidIban(d.iban)) return toast.error("L'IBAN inserito non è valido: controlla formato e cifre di controllo.");
    setSaving(true);
    try { await api.put(`/buildings/${building.id}`, d); toast.success("Condominio aggiornato"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };
  return (
    <>
      <button data-testid="edit-building-btn" onClick={() => setOpen(true)} className="text-muted-foreground hover:text-[#007BFF] p-1"><Pencil className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-building-dialog">
          <DialogHeader><DialogTitle>Modifica condominio</DialogTitle>
            <DialogDescription>Aggiorna nome, indirizzo, città, codice fiscale e IBAN del condominio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {FIELD("Nome *", "name", d, setD)}
            <AddressAutocompleteField data={d} onChange={setD} label="Indirizzo" />
            {FIELD("Città", "citta", d, setD)}
            <ProvinciaField data={d} onChange={setD} />
            {FIELD("Codice Fiscale", "codice_fiscale", d, setD, { className: "h-9 mt-1 font-mono uppercase" })}
            <IbanField label="IBAN" testId="edit-building-iban" value={d.iban} onChange={(v) => setD((p) => ({ ...p, iban: v }))} />
            {FIELD("Codice SDI (facoltativo)", "sdi", d, setD, { className: "h-9 mt-1 font-mono uppercase" })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-edit-building-btn" onClick={save} disabled={saving} className="bg-[#007BFF] hover:bg-[#0056B3]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
